import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import type { AudiobookTranscript, TranscriptChapter, TranscriptLine } from '@/types/audiobook-transcript'

type TranscriptViewProps = {
  transcript: AudiobookTranscript
  currentTime: number
  onSeek: (seconds: number) => void
}

/** 現在の再生時間に該当する行をバイナリサーチで特定する */
const findCurrentLineIndex = (lines: TranscriptLine[], time: number): number => {
  if (lines.length === 0) return -1

  let lo = 0
  let hi = lines.length - 1
  let result = -1

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (lines[mid].start <= time) {
      result = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  return result
}

/**
 * Apple Music歌詞風の文字起こし表示コンポーネント
 *
 * - 現在再生中の行をハイライト＋自動スクロール
 * - チャプター境界にヘッダーを挿入
 * - 行タップでシーク
 */
export const TranscriptView = ({ transcript, currentTime, onSeek }: TranscriptViewProps) => {
  const lineRefs = useRef<Map<number, HTMLDivElement | null>>(new Map())
  const [userScrolling, setUserScrolling] = useState(false)
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { lines, chapters } = transcript

  // 現在の行インデックス
  const currentLineIndex = useMemo(() => findCurrentLineIndex(lines, currentTime), [lines, currentTime])

  // チャプター境界マップ: 各チャプターの先頭行の行インデックス → Chapter
  const chapterHeaders = useMemo(() => {
    if (chapters.length === 0) return new Map<number, TranscriptChapter>()

    const result = new Map<number, TranscriptChapter>()
    let lastChapter = -1

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].chapter !== lastChapter) {
        lastChapter = lines[i].chapter
        const ch = chapters[lastChapter]
        if (ch) result.set(i, ch)
      }
    }

    return result
  }, [lines, chapters])

  // ユーザーがスクロール操作中の検出（3秒間自動スクロールを抑制）
  const handleUserScroll = useCallback(() => {
    setUserScrolling(true)
    if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current)
    userScrollTimeoutRef.current = setTimeout(() => setUserScrolling(false), 3000)
  }, [])

  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current)
    }
  }, [])

  // 自動スクロール（ユーザー操作中は抑制）
  useEffect(() => {
    if (currentLineIndex < 0 || userScrolling) return

    const el = lineRefs.current.get(currentLineIndex)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [currentLineIndex, userScrolling])

  const handleLineClick = useCallback(
    (line: TranscriptLine) => {
      onSeek(line.start)
    },
    [onSeek],
  )

  if (lines.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>文字起こしデータがありません</p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-4 pb-20 pt-4" onScroll={handleUserScroll}>
      {lines.map((line, idx) => {
        const isActive = idx === currentLineIndex
        const isPast = idx < currentLineIndex
        const chapterHeader = chapterHeaders.get(idx)

        return (
          <div key={line.id}>
            {/* チャプターヘッダー */}
            {chapterHeader && (
              <div className="mt-6 mb-3 first:mt-0">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {chapterHeader.title}
                </h3>
                <div className="h-px bg-border mt-1" />
              </div>
            )}

            {/* 行 */}
            <div
              ref={(el) => {
                lineRefs.current.set(idx, el)
              }}
              className={cn(
                'py-1.5 px-2 rounded-md cursor-pointer transition-all duration-200 select-none',
                isActive && 'text-primary font-medium scale-[1.01]',
                isPast && !isActive && 'text-muted-foreground/60',
                !isActive && !isPast && 'text-foreground/80',
                'hover:bg-accent/50 active:bg-accent',
              )}
              onClick={() => handleLineClick(line)}
            >
              <p className={cn('text-sm leading-relaxed', isActive && 'text-base')}>{line.text}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
